import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Paper,
  Avatar,
  MenuItem,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useSelector, useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
  removeActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { findStrategy } from "../../component/Methods";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { selectFunds, selectStrategies, selectBrokers, selectLots, selectAccountingData } from "../../Redux/Reducers/selectors";

const ClosePositionDialog = ({ open, onClose, positionData }) => {
  const theme = useTheme();
  const accountingData = useSelector(selectAccountingData);
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);
  const brokers = useSelector(selectBrokers);
  const lots = useSelector(selectLots);
  const accFunds = accountingData[0]?.funds;
  const user = useSelector((state) => state?.users?.user);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    fund: "",
    strategy: "",
    action: "",
    ticker: "",
    broker: "",
    routing: "Manual", // Default to Manual
  });

  const [distributionRows, setDistributionRows] = useState([
    { account: "", shareCount: "" },
  ]);

  const [availableStrategies, setAvailableStrategies] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);

  // Initialize form data when position data changes
  useEffect(() => {
    if (positionData) {
      setFormData({
        fund: positionData.fundName || "",
        strategy: positionData.strategyName || "",
        action: positionData.shares > 0 ? "Sell" : "Cover", // Close position: sell long, cover short
        ticker: positionData.tickerSymbol || "",
        broker: "",
        routing: "Manual", // Default to Manual
      });

      // Set distribution rows based on position data - but don't preselect account
      if (positionData.fundName && positionData.strategyName) {
        setDistributionRows([
          {
            account: "", // Don't preselect account
            shareCount: Math.abs(positionData.shares || 0).toString(),
          },
        ]);
      }
    }
  }, [positionData]);

  // Update strategies when fund changes
  useEffect(() => {
    if (formData.fund && funds) {
      const selectedFundByAbbr = funds.find(
        (fund) => fund.abbreviation === formData.fund
      );
      if (selectedFundByAbbr?.fundId) {
        let fundStrategies = [];

        // First try to get strategies from accountingData if available
        if (accFunds && accFunds.length > 0) {
          const selectedFund = accFunds?.find(
            (fund) => fund?.fund === selectedFundByAbbr?.fundId
          );

          if (
            selectedFund?.strategyAUMs &&
            Object.keys(selectedFund.strategyAUMs).length > 0
          ) {
            // Get strategy names from the strategy IDs in strategyAUMs
            const strategyIds = Object.keys(selectedFund.strategyAUMs);

            fundStrategies = strategyIds.map((strategyId) => ({
              strategyId: strategyId,
              name: findStrategy(strategyId, strategies) || "N/A",
            }));
          }
        }

        if (fundStrategies.length > 0) {
          setAvailableStrategies(fundStrategies);
        } else {
          setAvailableStrategies([]);
          setFormData((prev) => ({ ...prev, strategy: "" }));
        }

        // Update available accounts based on selected fund
        if (
          selectedFundByAbbr.accounts &&
          Object.keys(selectedFundByAbbr.accounts).length > 0
        ) {
          const accountNames = Object.keys(selectedFundByAbbr.accounts);
          setAvailableAccounts(accountNames);
        } else {
          setAvailableAccounts([]);
        }
      } else {
        setAvailableStrategies([]);
        setAvailableAccounts([]);
        setFormData((prev) => ({ ...prev, strategy: "" }));
      }
    } else {
      setAvailableStrategies([]);
      setAvailableAccounts([]);
      setFormData((prev) => ({ ...prev, strategy: "" }));
    }
  }, [formData.fund, funds, accountingData, strategies]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleDistributionChange = (field) => (event) => {
    setDistributionRows((prev) =>
      prev.map((row, i) =>
        i === 0 ? { ...row, [field]: event.target.value } : row
      )
    );
  };

  const handleSubmit = () => {
    // Validate required fields
    if (
      !formData.fund ||
      !formData.action ||
      !formData.ticker ||
      !formData.broker
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate distribution rows
    const hasEmptyDistribution = distributionRows.some(
      (row) => !row.account || !row.shareCount
    );
    if (hasEmptyDistribution) {
      alert("Please fill in all distribution account and share count fields");
      return;
    }

    // Find the selected fund to get fundId
    const selectedFund = funds.find(
      (fund) => fund.abbreviation === formData.fund
    );
    if (!selectedFund?.fundId) {
      alert("Invalid fund selected");
      return;
    }

    // Find the selected strategy to get strategyId (if provided)
    let selectedStrategy = null;
    if (formData.strategy) {
      selectedStrategy = strategies.find(
        (strategy) =>
          strategy.name === formData.strategy ||
          strategy.strategyId === formData.strategy ||
          strategy.id === formData.strategy
      );
    }

    // Build the distribution object with proper keys
    const distribution = {};
    distributionRows.forEach((row) => {
      if (row.account && row.shareCount) {
        // Build distribution key: account_name#fund_id#0#strategy_id
        let distributionKey = `${row.account}#${selectedFund.fundId}#0`;

        // Add strategy ID if strategy is selected
        if (selectedStrategy) {
          const strategyId = selectedStrategy.strategyId || selectedStrategy.id;
          distributionKey += `#${strategyId}`;
        }

        distribution[distributionKey] = parseInt(row.shareCount);
      }
    });

    // Get action and position based on your mappings
    const getActionAndPosition = (action) => {
      switch (action) {
        case "Buy":
          return { action: 0, position: 0 };
        case "Sell":
          return { action: 3, position: 0 };
        case "Short":
          return { action: 3, position: 1 };
        case "Cover":
          return { action: 0, position: 1 };
        default:
          return { action: 0, position: 0 };
      }
    };

    const { action: actionValue, position } = getActionAndPosition(
      formData.action
    );

    // Get broker ID
    const broker = brokers?.find((b) => b.abbreviation === formData.broker);

    if (!broker?.brokerId) {
      alert("Invalid broker selected");
      return;
    }
    const brokerId = broker.brokerId;

    const orderData = {
      distribution,
      action: actionValue,
      position: position,
      broker: brokerId,
      type: 1,
      typePrice: 0,
      instructions: "best way",
      routing: formData.routing === "Auto" ? 0 : 1, // 0 for Auto, 1 for Manual
      ticker: {
        symbol: formData.ticker.toLowerCase(),
        investment: 4,
        exchange: 4,
        currency: 7,
      },
      user: user?.username || user?.email,
    };

    const requestData = {
      action: "new_order",
      data: {
        orders: [orderData],
      },
    };

    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Submitting close position order..."));
    dispatch(addActiveRequests("new_order"));

    sendWebSocketMessage(JSON.stringify(requestData));
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      fund: "",
      strategy: "",
      action: "",
      ticker: "",
      broker: "",
      routing: "Manual",
    });
    setDistributionRows([{ account: "", shareCount: "" }]);
    setAvailableStrategies([]);
    setAvailableAccounts([]);
    onClose();
  };

  // Get unique tickers from lots data (memoized)
  const uniqueTickers = React.useMemo(() => {
    if (!lots || lots.length === 0) return [];

    const tickerSet = new Set();
    const tickerObjects = [];

    lots.forEach((lot) => {
      if (
        lot?.ticker?.symbol &&
        !tickerSet.has(lot.ticker.symbol.toLowerCase())
      ) {
        // Only include tickers that start with alphabets (A-Z, a-z)
        const symbol = lot.ticker.symbol;
        if (/^[A-Za-z]/.test(symbol)) {
          tickerSet.add(symbol.toLowerCase());
          tickerObjects.push({
            symbol: symbol.toUpperCase(),
            investment: lot.ticker.investment,
            currency: lot.ticker.currency,
            exchange: lot.ticker.exchange,
          });
        }
      }
    });

    return tickerObjects.sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [lots]);

  // Sort options for dropdowns (create new arrays to avoid mutating Redux state)
  const sortedFunds = funds
    ? [...funds]
        .filter((fund) => fund && fund.abbreviation) // Filter out null/undefined items
        .sort((a, b) =>
          (a.abbreviation || "").localeCompare(b.abbreviation || "")
        )
    : [];

  const sortedBrokers = brokers
    ? [...brokers]
        .filter((broker) => broker && broker.abbreviation) // Filter out null/undefined items
        .sort((a, b) =>
          (a.abbreviation || "").localeCompare(b.abbreviation || "")
        )
    : [];

  const sortedStrategies = [...availableStrategies]
    .filter((strategy) => strategy && strategy.name) // Filter out null/undefined items
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const actionOptions = ["Buy", "Cover", "Sell", "Short"]; // Already sorted alphabetically
  const routingOptions = ["Auto", "Manual"];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.red[100],
              width: 48,
              height: 48,
            }}
          >
            {/* instead of add icon, use the cross icon from MUI */}
            <CloseIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Close Position
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create an order to close existing position
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, overflowY: "visible" }}>
        <Grid container spacing={1.5}>
          {/* Fund Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedFunds}
              getOptionLabel={(option) => option.abbreviation}
              value={
                sortedFunds.find(
                  (fund) => fund.abbreviation === formData.fund
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  fund: newValue?.abbreviation || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fund"
                  variant="outlined"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  size="small"
                  required
                />
              )}
            />
          </Grid>

          {/* Strategy Dropdown (conditional) */}
          {availableStrategies.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={sortedStrategies}
                getOptionLabel={(option) => option.name}
                value={
                  sortedStrategies.find(
                    (strategy) => strategy.name === formData.strategy
                  ) || null
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    strategy: newValue?.name || "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Strategy"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Action Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={actionOptions}
              value={formData.action || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  action: newValue || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Ticker Input */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={uniqueTickers}
              getOptionLabel={(option) => option.symbol}
              value={
                uniqueTickers.find(
                  (ticker) => ticker.symbol === formData.ticker
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  ticker: newValue?.symbol || "",
                });
              }}
              freeSolo
              inputValue={formData.ticker}
              onInputChange={(event, newInputValue) => {
                setFormData({
                  ...formData,
                  ticker: newInputValue.toUpperCase(),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ticker"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Broker Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedBrokers}
              getOptionLabel={(option) => option.abbreviation}
              value={
                sortedBrokers.find(
                  (broker) => broker.abbreviation === formData.broker
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  broker: newValue?.abbreviation || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Broker"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Routing Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={routingOptions}
              value={formData.routing || null}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  routing: newValue || "Manual",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Routing"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Account and Share Count Row */}
          <Grid size={{ xs: 6 }}>
            <Autocomplete
              options={availableAccounts}
              value={distributionRows[0]?.account || null}
              onChange={(event, newValue) => {
                handleDistributionChange("account")({
                  target: { value: newValue || "" },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Share Count"
              type="number"
              value={distributionRows[0]?.shareCount || ""}
              onChange={handleDistributionChange("shareCount")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 120,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Create Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosePositionDialog;
